import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LockIcon, ZapIcon, ShieldIcon, StarIcon } from 'lucide-react';
type PlanId = 'base' | 'pro' | 'biblioteca';
interface PlanDetail {
  icon: string;
  text: string;
  isHighlight?: boolean;
}
interface Plan {
  id: PlanId;
  name: string;
  price: string;
  originalPrice: string;
  summary: string;
  link: string;
  savings?: string;
  details: PlanDetail[];
}
const plans: Plan[] = [{
  id: 'base',
  name: 'Plan Base',
  price: '18.990',
  originalPrice: '49.990',
  summary: '4 Módulos + 1 Bono',
  link: 'https://checkout.empezahoy.shop/cart/48129330675944:1',
  details: [{
    icon: '✅',
    text: 'Módulo 1 – Informes terapéuticos profesionales'
  }, {
    icon: '✅',
    text: 'Módulo 2 – Evaluaciones terapéuticas'
  }, {
    icon: '✅',
    text: 'Módulo 3 – Registro de sesiones terapéuticas'
  }, {
    icon: '✅',
    text: 'Módulo 4 – Banco de actividades terapéuticas'
  }, {
    icon: '✅',
    text: 'Bono 1 – Banco de Actividades de Motricidad Fina'
  }]
}, {
  id: 'pro',
  name: 'Plan PRO',
  price: '23.990',
  originalPrice: '69.990',
  summary: '5 Módulos + 3 Bonos completos',
  link: 'https://checkout.empezahoy.shop/cart/48129316815080:1',
  savings: 'AHORRÁS $15.000',
  details: [{
    icon: '✨',
    text: 'Todo lo del Plan Base +',
    isHighlight: true
  }, {
    icon: '✅',
    text: 'Módulo 5 – Seguimiento del progreso del paciente'
  }, {
    icon: '✅',
    text: 'Bono 2 – Kit Evaluación de Motricidad Fina'
  }, {
    icon: '✅',
    text: 'Bono 3 – Banco de Actividades de Estimulación Cognitiva'
  }, {
    icon: '✅',
    text: 'Acceso de por vida + actualizaciones gratuitas'
  }]
}, {
  id: 'biblioteca',
  name: 'Plan Biblioteca Completa',
  price: '29.990',
  originalPrice: '89.990',
  summary: 'Plan PRO + 3 Bibliotecas terapéuticas',
  link: 'https://checkout.empezahoy.shop/cart/48129333330152:1',
  details: [{
    icon: '✨',
    text: 'Todo lo del Plan PRO +',
    isHighlight: true
  }, {
    icon: '📚',
    text: 'Banco de Actividades Terapéuticas Infantil – Motricidad fina, coordinación y estimulación cognitiva con niños'
  }, {
    icon: '📚',
    text: 'Banco de Actividades Terapéuticas para Adolescentes – Habilidades funcionales, coordinación y estimulación cognitiva'
  }, {
    icon: '📚',
    text: 'Banco de Actividades Terapéuticas para Adultos – Rehabilitación funcional, autonomía y estimulación cognitiva'
  }, {
    icon: '💡',
    text: 'Biblioteca completa de recursos para pacientes de distintas edades'
  }]
}];
export function PricingSection() {
  const [selectedPlanId, setSelectedPlanId] = useState<PlanId>('pro');
  const selectedPlan = plans.find(p => p.id === selectedPlanId)!;
  return <section id="comprar" className="py-16 md:py-24 px-4 bg-cream" data-id="element-93">
      <div className="max-w-3xl mx-auto" data-id="element-94">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10" data-id="element-95">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-olive-dark mb-4" data-id="element-96">
            Elegí tu plan
          </h2>
          <p className="text-lg text-olive/80" data-id="element-97">
            Acceso inmediato y de por vida. Pago único, sin suscripciones.
          </p>
        </motion.div>

        {/* Plan Cards */}
        <div className="space-y-4 mb-8" data-id="element-98">
          {plans.map(plan => {
          const isSelected = selectedPlanId === plan.id;
          return <motion.div key={plan.id} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} onClick={() => setSelectedPlanId(plan.id)} className={`relative cursor-pointer rounded-2xl p-5 md:p-6 transition-all duration-300 border-2 flex items-center gap-4 md:gap-6 ${isSelected ? 'border-golden bg-golden/5 shadow-md' : 'border-olive/10 bg-white hover:border-golden/40 shadow-sm'}`} data-id="element-99">
                {/* Radio Circle */}
                <div className="shrink-0 flex items-center justify-center" data-id="element-100">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-golden' : 'border-olive/30'}`} data-id="element-101">
                    {isSelected && <motion.div layoutId="radio-fill" className="w-3 h-3 bg-golden rounded-full" data-id="element-102" />}
                  </div>
                </div>

                {/* Plan Info */}
                <div className="flex-1 min-w-0" data-id="element-103">
                  {plan.id === 'pro' && <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-104">
                      <span className="inline-flex items-center gap-1 bg-golden text-white text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-105">
                        <StarIcon className="w-3 h-3 fill-white" data-id="element-106" />
                        Más Elegido
                      </span>
                      {plan.savings && <span className="bg-olive/10 text-olive-dark text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-107">
                          {plan.savings}
                        </span>}
                    </div>}
                  <h3 className="text-xl md:text-2xl font-serif font-bold text-olive-dark leading-tight" data-id="element-108">
                    {plan.name}
                  </h3>
                  <p className="text-sm md:text-base text-olive/70 mt-1" data-id="element-109">
                    {plan.summary}
                  </p>
                </div>

                {/* Price */}
                <div className="text-right shrink-0" data-id="element-110">
                  <div className="text-sm md:text-base text-olive/50 line-through font-medium mb-0.5" data-id="element-111">
                    ${plan.originalPrice}
                  </div>
                  <div className="text-2xl md:text-4xl font-bold text-golden tracking-tight" data-id="element-112">
                    ${plan.price}
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        {/* CTA Button */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-8" data-id="element-113">
          <a href={selectedPlan.link} className="block w-full text-center bg-golden hover:bg-golden-hover text-white py-5 md:py-6 rounded-full text-lg md:text-2xl font-bold uppercase tracking-wide transition-all shadow-lg hover:shadow-xl hover:-translate-y-1" data-id="element-114">
            {selectedPlanId === 'base' && '👉quiero empezar con el plan básico'}
            {selectedPlanId === 'pro' && '👉SI, Quiero el sistema PRO'}
            {selectedPlanId === 'biblioteca' && '👉Si, Quiero la biblioteca completa'}
          </a>
        </motion.div>

        {/* Animated Details Section */}
        <AnimatePresence mode="wait" data-id="element-115">
          <motion.div key={selectedPlan.id} initial={{
          opacity: 0,
          height: 0,
          y: -10
        }} animate={{
          opacity: 1,
          height: 'auto',
          y: 0
        }} exit={{
          opacity: 0,
          height: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} className="overflow-hidden mb-8" data-id="element-116">
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-olive/10 shadow-sm" data-id="element-117">
              <h4 className="font-bold text-olive-dark mb-5 text-lg border-b border-olive/10 pb-3" data-id="element-118">
                ¿Qué incluye el {selectedPlan.name}?
              </h4>
              <ul className="space-y-4" data-id="element-119">
                {selectedPlan.details.map((detail, index) => <li key={index} className="flex items-start gap-3" data-id="element-120">
                    <span className="shrink-0 mt-0.5 text-lg" data-id="element-121">
                      {detail.icon}
                    </span>
                    <span className={`text-base leading-relaxed ${detail.isHighlight ? 'font-bold text-olive-dark' : 'text-olive/80'}`} data-id="element-122">
                      {detail.text}
                    </span>
                  </li>)}
              </ul>

              {selectedPlan.id === 'pro' && <p className="text-center italic text-olive/60 mt-8 text-sm font-medium" data-id="element-123">
                  La opción más elegida por terapeutas ocupacionales
                </p>}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Trust Badges */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="flex flex-wrap items-center justify-center gap-4 md:gap-8 text-sm font-medium text-olive/70 bg-olive/5 py-4 px-6 rounded-2xl border border-olive/10" data-id="element-124">
          <div className="flex items-center gap-2" data-id="element-125">
            <LockIcon className="w-4 h-4 text-golden" data-id="element-126" />
            <span data-id="element-127">Pago seguro</span>
          </div>
          <div className="hidden md:block w-1 h-1 bg-olive/20 rounded-full" data-id="element-128" />
          <div className="flex items-center gap-2" data-id="element-129">
            <ZapIcon className="w-4 h-4 text-golden" data-id="element-130" />
            <span data-id="element-131">Acceso inmediato</span>
          </div>
          <div className="hidden md:block w-1 h-1 bg-olive/20 rounded-full" data-id="element-132" />
          <div className="flex items-center gap-2" data-id="element-133">
            <ShieldIcon className="w-4 h-4 text-golden" data-id="element-134" />
            <span data-id="element-135">Garantía 7 días</span>
          </div>
        </motion.div>
      </div>
    </section>;
}